import { Keyboard, X } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const SECTIONS = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 'l'], description: 'Go to Loan List' },
      { keys: ['g', 'b'], description: 'Go to Borrowers' },
      { keys: ['g', 'k'], description: 'Go to Kanban Board' },
      { keys: ['g', 'r'], description: 'Go to Reports' },
      { keys: ['g', 'c'], description: 'Go to Collections' },
      { keys: ['g', 'i'], description: 'Go to Data Import' },
      { keys: ['g', 'a'], description: 'Go to Activity Log' },
      { keys: ['g', 's'], description: 'Go to Settings' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['⌘', 'K'], description: 'Open command palette' },
      { keys: ['n', 'l'], description: 'New loan' },
      { keys: ['n', 'b'], description: 'New borrower' },
      { keys: ['?'], description: 'Show this help' },
    ],
  },
  {
    title: 'Command Palette',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Navigate results' },
      { keys: ['↵'], description: 'Open selected result' },
      { keys: ['Esc'], description: 'Close palette' },
    ],
  },
];

export function KeyboardShortcutsHelp({ open, onClose }: KeyboardShortcutsHelpProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <Keyboard className="w-4.5 h-4.5 text-indigo-600" />
            <h2 className="text-sm font-semibold text-gray-900">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-5">
          {SECTIONS.map(section => (
            <div key={section.title}>
              <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                {section.title}
              </h3>
              <div className="space-y-1.5">
                {section.shortcuts.map(shortcut => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="text-xs text-gray-600">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, i) => (
                        <span key={i}>
                          <kbd className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10px] font-mono font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded">
                            {key}
                          </kbd>
                          {i < shortcut.keys.length - 1 && (
                            <span className="text-[10px] text-gray-300 mx-0.5">then</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
          <p className="text-[10px] text-gray-400 text-center">
            Press{' '}
            <kbd className="inline px-1 py-0.5 text-[10px] font-mono bg-gray-200 rounded">?</kbd>{' '}
            anytime to show this help
          </p>
        </div>
      </div>
    </div>
  );
}
